import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ShoppingBag } from "@phosphor-icons/react";
import { useProfileParticipationsRows } from "../hooks/useProfileParticipationsRows.js";
import { hasMarketplaceJwtCredentials, saveTokenForGiveaway } from "../lib/wicoopProfileStorage.js";
import {
  canOpenParticipation,
  cashbackPercentLabel,
  cashLabel,
  filterParticipationsByChip,
  formatRowTime,
  formatRub,
  groupParticipations,
  inWorkRubTotal,
  paidRubTotal,
  participationCounts,
  rowThumbUrl,
  shortGiveawayId,
  statusKey,
  statusMeta,
} from "../utils/purchasesUi.js";
import styles from "./PurchasesPage.module.css";

const CHIPS = [
  { id: "all", label: "Все" },
  { id: "paid", label: "Выплачено" },
  { id: "review", label: "На проверке" },
  { id: "rejected", label: "Отклонено" },
];

function pillClass(tone) {
  if (tone === "success") return styles.pillSuccess;
  if (tone === "warn") return styles.pillWarn;
  if (tone === "danger") return styles.pillDanger;
  return styles.pillMuted;
}

export default function PurchasesPage() {
  const navigate = useNavigate();
  const [chip, setChip] = useState("all");
  const { rows, loading, error, reload } = useProfileParticipationsRows();
  const authed = hasMarketplaceJwtCredentials();

  const counts = useMemo(() => participationCounts(rows), [rows]);
  const paidRub = useMemo(() => paidRubTotal(rows), [rows]);
  const inWorkRub = useMemo(() => inWorkRubTotal(rows), [rows]);
  const filtered = useMemo(() => filterParticipationsByChip(rows, chip), [rows, chip]);
  const grouped = useMemo(() => groupParticipations(filtered), [filtered]);

  function openRow(row) {
    const token = row.verificationToken;
    if (token) saveTokenForGiveaway(row.giveawayId, token);
    if (canOpenParticipation(row)) {
      navigate(`/giveaway/${row.giveawayId}/participate`);
      return;
    }
    navigate(`/giveaway/${row.giveawayId}`);
  }

  if (!authed) {
    return (
      <div className={styles.loginWrap}>
        <div className={styles.loginIcon}>
          <ShoppingBag size={34} weight="regular" aria-hidden />
        </div>
        <h2 className={styles.loginTitle}>Войдите в аккаунт</h2>
        <p className={styles.loginLead}>
          Здесь будут ваши заявки, статусы проверки и история выплат.
        </p>
        <button type="button" className={styles.loginBtn} onClick={() => navigate("/profile/login")}>
          Перейти в профиль
        </button>
      </div>
    );
  }

  return (
    <div className={styles.purchasesPage}>
      <div className={styles.topChrome}>
        <h1 className={styles.topTitle}>Покупки</h1>
        <p className={styles.topSub}>История заявок и выплат</p>
      </div>

      <div className={styles.summaryCard}>
        <div className={styles.summaryGlow} aria-hidden />
        <p className={styles.summaryKicker}>ВОЗВРАЩЕНО ВСЕГО</p>
        <p className={styles.summaryValue}>{formatRub(paidRub)}</p>
        <div className={styles.summaryStats}>
          <div>
            <p className={styles.summaryStatLabel}>ПОКУПОК</p>
            <p className={styles.summaryStatValue}>{counts.all}</p>
          </div>
          <div className={styles.summarySep} aria-hidden />
          <div>
            <p className={styles.summaryStatLabel}>В РАБОТЕ</p>
            <p className={styles.summaryStatValue}>{formatRub(inWorkRub)}</p>
          </div>
        </div>
      </div>

      <div className={styles.chipsRow} role="tablist" aria-label="Фильтр покупок">
        {CHIPS.map((c) => {
          const active = chip === c.id;
          const cnt = counts[c.id] ?? counts.all;
          return (
            <button
              key={c.id}
              type="button"
              role="tab"
              aria-selected={active}
              className={`${styles.chip} ${active ? styles.chipActive : ""}`}
              onClick={() => setChip(c.id)}
            >
              {c.label}
              <span className={`${styles.chipCount} ${active ? styles.chipCountActive : ""}`}>
                {cnt}
              </span>
            </button>
          );
        })}
      </div>

      {error ? (
        <p className={styles.error} role="alert">
          {error}{" "}
          <button type="button" className={styles.refreshBtn} onClick={reload}>
            Повторить
          </button>
        </p>
      ) : null}

      {loading && rows.length === 0 ? (
        <div className={styles.loadingWrap}>
          <div className={styles.spinner} aria-hidden />
          <p className={styles.status}>Загрузка…</p>
        </div>
      ) : null}

      {!loading && grouped.length === 0 ? (
        <div className={styles.emptyWrap}>
          <div className={styles.emptyIcon}>
            <ShoppingBag size={34} weight="regular" aria-hidden />
          </div>
          <h2 className={styles.emptyTitle}>Пока пусто</h2>
          <p className={styles.emptyLead}>
            Участвуйте в раздачах — здесь появятся ваши заявки и выплаты.
          </p>
        </div>
      ) : null}

      {grouped.length > 0 ? (
        <div className={styles.groups}>
          {grouped.map((g) => (
            <section key={g.title}>
              <h2 className={styles.groupTitle}>{g.title}</h2>
              <div className={styles.groupCard}>
                {g.items.map((it, idx) => {
                  const row = it.row;
                  const meta = statusMeta(statusKey(row), row);
                  const thumb = rowThumbUrl(row);
                  const pct = cashbackPercentLabel(row);
                  const when = formatRowTime(it.date);
                  const idLabel = shortGiveawayId(row.giveawayId);
                  const cash = cashLabel(row);
                  return (
                    <button
                      key={`${row.giveawayId}-${idx}`}
                      type="button"
                      className={styles.rowBtn}
                      onClick={() => openRow(row)}
                    >
                      <div className={`${styles.row} ${idx > 0 ? styles.rowDivider : ""}`}>
                        <div className={styles.thumbWrap}>
                          {thumb ? (
                            <img src={thumb} alt="" className={styles.thumb} loading="lazy" />
                          ) : (
                            <div className={styles.thumbPlaceholder}>🧾</div>
                          )}
                          {pct ? <span className={styles.pctBadge}>{pct}</span> : null}
                        </div>
                        <div className={styles.rowMain}>
                          <div className={styles.rowTitleLine}>
                            <span className={styles.rowTitle}>
                              {row.giveaway?.title ?? row.giveawayId}
                            </span>
                            <span className={`${styles.pill} ${pillClass(meta.tone)}`}>
                              {meta.label}
                            </span>
                          </div>
                          <p className={styles.rowMeta}>
                            {[when, idLabel].filter(Boolean).join(" · ")}
                          </p>
                          {cash ? <p className={styles.rowCash}>{cash}</p> : null}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </section>
          ))}
        </div>
      ) : null}
    </div>
  );
}
